import React, { FC, MouseEvent, ReactElement, useMemo } from "react";
import { AppPage } from "shared-ts-ui-app-page";
import {
  Box,
  Card,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Toolbar,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useDateTimeNow } from "shared-ts-utils-luxon";
import { BurnQualityContent } from "./BurnQualityContent";
import { BuildingSelect, FlueSelect } from "firefly-my-chimney-ts-building";
import { AddressId, FlueId } from "firefly-shared-ts-domain";
import { useResizeDetector } from "react-resize-detector";
import { ScrollableToolbar } from "shared-ts-mui";
import {
  ArraySetting,
  useUserSettingWithDefault,
} from "shared-ts-utils-user-settings";
import { ValueOption } from "shared-ts-property";
import TimelineRoundedIcon from "@mui/icons-material/TimelineRounded";
import BarChartRoundedIcon from "@mui/icons-material/BarChartRounded";
import {
  SizeClass,
  useComponentSizeFromDense,
  useResponsive,
  useWindowWidth,
} from "shared-ts-ui-responsive";
import { TooltipOrNot } from "shared-ts-ui-tooltip";
import { createLazyRoute } from "@tanstack/react-router";
import { useMyChimneyContext } from "my-chimney-ts-my-chimney-context";

export const HistoryPage: FC = () => {
  const { t } = useTranslation("my-chimney-ts-translations");
  const withAsSizeClass = useWindowWidth();
  const { width, ref } = useResizeDetector({ handleHeight: false });
  const now = useDateTimeNow();
  const responsive = useResponsive();
  const componentSize = useComponentSizeFromDense();
  const { t: t_firefly_shared_domain } = useTranslation(
    "firefly-shared-ts-domain"
  );
  const {
    buildings,
    selectedBuilding,
    setSelectedBuilding,
    selectedFlue,
    setSelectedFlue,
  } = useMyChimneyContext();

  const [diagramFeatures, setDiagramFeatures] = useUserSettingWithDefault(
    "HistoryPage.diagramFeatures",
    new ArraySetting<DiagramFeatureType>(),
    ["GoodInPercent"]
  );

  const goodInPercentChecked = diagramFeatures.some(
    (it) => it === "GoodInPercent"
  );

  const ignitionByQualityChecked = diagramFeatures.some(
    (it) => it === "IgnitionByQuality"
  );

  const diagramFeaturesValues: Array<
    ValueOption & { checked: boolean; disabled: boolean; icon: ReactElement }
  > = useMemo(() => {
    return [
      {
        id: "GoodInPercent",
        label: t_firefly_shared_domain("Good ignitions in %"),
        checked: goodInPercentChecked,
        disabled: goodInPercentChecked && !ignitionByQualityChecked,
        icon: <TimelineRoundedIcon />,
      },
      {
        id: "IgnitionByQuality",
        label: t_firefly_shared_domain("Ignitions by quality"),
        checked: ignitionByQualityChecked,
        disabled: ignitionByQualityChecked && !goodInPercentChecked,
        icon: <BarChartRoundedIcon />,
      },
    ];
  }, [goodInPercentChecked, ignitionByQualityChecked, t_firefly_shared_domain]);

  const handleBuildingChange = (value: AddressId) => {
    setSelectedBuilding(value);
  };
  const handleSelectedFlue = (flueId: FlueId) => {
    setSelectedFlue(flueId);
  };

  const handleDiagramFeaturesToggleGroupChange = (
    event: MouseEvent,
    newValue: Array<string>
  ) => {
    const newDiagramFeatures = newValue as DiagramFeaturesType;
    setDiagramFeatures(newDiagramFeatures);
  };

  return (
    <AppPage
      $key={"HistoryPage"}
      name={t("History")}
      hideName
      mainPane={
        <Box
          sx={{
            flexGrow: 1,
            minWidth: 0,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "stretch",
          }}
        >
          <Stack
            direction={"column"}
            gap={1}
            useFlexGap
            sx={{ flexGrow: 1, minHeight: 0 }}
            ref={ref}
          >
            <Toolbar
              disableGutters
              variant={responsive.toolbar.variant}
              sx={{ justifyContent: "center" }}
            >
              <BuildingSelect
                buildings={buildings}
                selected={selectedBuilding.id}
                onChange={handleBuildingChange}
              />
              {selectedBuilding.getNumberOfFlues() > 1 && (
                <FlueSelect
                  building={selectedBuilding}
                  selectedFlue={selectedFlue}
                  onSelectFlue={handleSelectedFlue}
                  sx={{ ml: 1 }}
                />
              )}
            </Toolbar>
            <Card sx={{ display: "flex", flex: 1, flexGrow: 1, p: 2 }}>
              <BurnQualityContent
                customerEnvironment={selectedBuilding.environment.id}
                flue={selectedFlue}
                showBurnQualityCount={ignitionByQualityChecked}
                showGoodInPercent={goodInPercentChecked}
                layout={withAsSizeClass}
              />
            </Card>
            <ScrollableToolbar
              ToolbarProps={{
                sx: { justifyContent: "center" },
              }}
            >
              <ToggleButtonGroup
                value={diagramFeatures}
                onChange={handleDiagramFeaturesToggleGroupChange}
              >
                {diagramFeaturesValues.map((diagramFeature) => {
                  return (
                    <TooltipOrNot
                      key={diagramFeature.id}
                      title={
                        withAsSizeClass === SizeClass.Compact
                          ? diagramFeature.label
                          : undefined
                      }
                    >
                      <ToggleButton
                        color={"primary"}
                        value={diagramFeature.id}
                        key={diagramFeature.id}
                        size={componentSize}
                        sx={{ gap: 1 }}
                      >
                        {diagramFeature.icon}{" "}
                        {withAsSizeClass !== SizeClass.Compact &&
                          diagramFeature.label}
                      </ToggleButton>
                    </TooltipOrNot>
                  );
                })}
              </ToggleButtonGroup>
            </ScrollableToolbar>
          </Stack>
        </Box>
      }
    />
  );
};

type DiagramFeatureType = "IgnitionByQuality" | "GoodInPercent";

type DiagramFeaturesType =
  | ["IgnitionByQuality", "GoodInPercent"]
  | ["IgnitionByQuality"]
  | ["GoodInPercent"];

export const HistoryPageRoute = createLazyRoute("/_myChimneyContext/history")({
  component: HistoryPage,
});
